import { Injectable, inject, signal } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import type { AbstractAgent } from "@ag-ui/client";
import type { AgentKitService } from "../../../agent-ui";
import { AgentDebugDialogComponent } from "../components/debug/agent-debug-dialog/agent-debug-dialog.component";
import { ChatReportDialogComponent } from "../components/debug/report-dialog/chat-report-dialog.component";
import type { ChatRunTraceEntry } from "../components/debug/report-dialog/chat-trace-report";

export interface DebugEvent {
  id: number;
  timestamp: Date;
  type: string;
  content: unknown;
  /** "lifecycle" rows come from synthetic hooks (run/messages/state changes); "stream" rows are raw AG-UI events. */
  category: "stream" | "lifecycle";
  /** Pre-formatted human-readable summary — lifecycle rows carry one, stream rows fall back to generic summarization. */
  summary?: string;
  /** Precomputed lowercase haystack (type + summary + full content), built once at log time so search never re-serializes per keystroke. */
  searchText: string;
}

export interface PipelineDebugEvent {
  id: number;
  timestamp: Date;
  jobId: string;
  type: string;
  content: unknown;
  searchText: string;
}

const MAX_EVENTS = 5000;
const MAX_PIPELINE_EVENTS = 5000;

function buildSearchText(type: string, summary: string | undefined, content: unknown): string {
  let contentStr: string;
  try {
    contentStr = JSON.stringify(content) ?? "";
  } catch {
    contentStr = String(content);
  }
  return `${type} ${summary ?? ""} ${contentStr}`.toLowerCase();
}

@Injectable({ providedIn: "root" })
export class DebugStreamService {
  readonly events = signal<DebugEvent[]>([]);

  readonly pipelineEvents = signal<PipelineDebugEvent[]>([]);

  readonly latestState = signal<unknown>(null);

  readonly latestContext = signal<unknown>(null);

  /** When true, clears all events automatically when a new run starts or the conversation (thread) changes. */
  readonly autoClear = signal(true);

  /** Thread id registered by the active chat container — identity source for the report dialog. */
  readonly traceThreadId = signal<string | null>(null);

  /** Agent id registered by the active chat container — identity source for the report dialog. */
  readonly traceAgentId = signal<string | null>(null);

  /**
   * Session run registry, newest-first. RUN_STARTED → "running", RUN_FINISHED → "success"
   * (matched by runId), RUN_ERROR → "error" on the latest still-running entry (the event
   * carries no runId per the AG-UI schema). Kept unfiltered — the report scopes by thread.
   */
  readonly runTrace = signal<ChatRunTraceEntry[]>([]);

  private readonly dialog = inject(MatDialog);

  #counter = 0;

  #pipelineCounter = 0;

  #runStartTime: number | null = null;

  #unsubscribeFn?: () => void;

  #registeredKit: AgentKitService | null = null;

  #registeredAgentId = "";

  #lastThreadId: string | null = null;

  /** Called when the map/agent context sent to the runtime changes. */
  updateContext(context: unknown): void {
    this.latestContext.set(context);
  }

  /** Called by ChatContainerComponent to register the active agent context. Clears the panel on a conversation switch when autoClear is on. */
  register(agentKit: AgentKitService, agentId: string, threadId: string): void {
    this.#registeredKit = agentKit;
    this.#registeredAgentId = agentId;
    this.traceAgentId.set(agentId);
    this.traceThreadId.set(threadId);

    if (this.#lastThreadId !== null && threadId !== this.#lastThreadId && this.autoClear()) {
      this.clear();
    }
    this.#lastThreadId = threadId;
  }

  hookAgent(agent: AbstractAgent): void {
    this.#unsubscribeFn?.();

    const log = (type: string, content: unknown) => this.#log(type, content);

    const subscription = agent.subscribe({
      onRunStartedEvent: ({ event }) => {
        log("RUN_STARTED", event);
        this.#traceRunStart(event);
      },
      onRunFinishedEvent: ({ event }) => {
        log("RUN_FINISHED", event);
        this.#traceRunEnd(event, "success");
      },
      onRunErrorEvent: ({ event }) => {
        log("RUN_ERROR", event);
        this.#traceRunError();
      },
      onStepStartedEvent: ({ event }) => log("STEP_STARTED", event),
      onStepFinishedEvent: ({ event }) => log("STEP_FINISHED", event),
      onTextMessageStartEvent: ({ event }) => log("TEXT_MESSAGE_START", event),
      onTextMessageContentEvent: ({ event, textMessageBuffer }) =>
        log("TEXT_MESSAGE_CONTENT", { ...event, _buffer: textMessageBuffer }),
      onTextMessageEndEvent: ({ event }) => log("TEXT_MESSAGE_END", event),
      onToolCallStartEvent: ({ event }) => log("TOOL_CALL_START", event),
      onToolCallArgsEvent: ({ event, toolCallBuffer, toolCallName, partialToolCallArgs }) =>
        log("TOOL_CALL_ARGS", {
          ...event,
          _buffer: toolCallBuffer,
          _name: toolCallName,
          _partial: partialToolCallArgs,
        }),
      onToolCallEndEvent: ({ event, toolCallName, toolCallArgs }) =>
        log("TOOL_CALL_END", { ...event, _name: toolCallName, _args: toolCallArgs }),
      onToolCallResultEvent: ({ event }) => log("TOOL_CALL_RESULT", event),
      onStateSnapshotEvent: ({ event }) => {
        const snap = (event as { snapshot: unknown }).snapshot ?? event;
        const isStreaming = this.#runStartTime !== null;
        const blockTypes = this.#enrichedBlockTypes(snap);

        log("STATE_SNAPSHOT", {
          ...(event as object),
          _meta: { duringStream: isStreaming, enrichedBlocks: blockTypes },
        });
        this.latestState.set(snap);
        this.#logLifecycle(
          "SNAPSHOT",
          `${isStreaming ? "during stream" : "post-stream"}` +
            `${blockTypes.length ? ` · blocks: [${blockTypes.join(", ")}]` : ""}`,
          { duringStream: isStreaming, blockTypes },
        );
      },
      onStateDeltaEvent: ({ event }) => log("STATE_DELTA", event),
      onMessagesSnapshotEvent: ({ event }) => log("MESSAGES_SNAPSHOT", event),
      onCustomEvent: ({ event }) => log("CUSTOM", event),
      onRawEvent: ({ event }) => log("RAW", event),
      onRunInitialized: () => {
        if (this.autoClear()) this.clear();
        this.#runStartTime = Date.now();
        this.#logLifecycle("RUN_INITIALIZED", "Run started", null);
      },
      onRunFinalized: () => {
        const ms = this.#runStartTime !== null ? Date.now() - this.#runStartTime : null;
        this.#runStartTime = null;
        this.#logLifecycle("RUN_FINALIZED", ms !== null ? `Run finished in ${ms}ms` : "Run finished", null);
      },
      onRunFailed: () => {
        const ms = this.#runStartTime !== null ? Date.now() - this.#runStartTime : null;
        this.#runStartTime = null;
        this.#logLifecycle("RUN_FAILED", ms !== null ? `Run failed after ${ms}ms` : "Run failed", null);
      },
      onMessagesChanged: () => {
        const msgs: any[] = (agent as any).messages ?? [];
        const count = msgs.length;
        const last = msgs[count - 1];
        let lastSummary = "";
        if (last) {
          const raw = last.content;
          const text =
            typeof raw === "string"
              ? raw
              : Array.isArray(raw)
              ? raw.map((c: any) => c.text ?? c.content ?? "").join("")
              : "";
          lastSummary = `[${last.role}] ${text.slice(0, 100)}`;
        }
        this.#logLifecycle(
          "MESSAGES_CHANGED",
          `${count} message${count !== 1 ? "s" : ""}${lastSummary ? ` — ${lastSummary}` : ""}`,
          { count, lastMessage: last ?? null },
        );
      },
      onStateChanged: () => {
        const state = (agent as any).state;
        const keys = state && typeof state === "object" ? Object.keys(state) : [];
        const prev = this.latestState();
        const prevKeys = prev && typeof prev === "object" ? Object.keys(prev as object) : [];
        const added = keys.filter((k) => !prevKeys.includes(k));
        const removed = prevKeys.filter((k) => !keys.includes(k));
        const parts: string[] = [`keys: [${keys.join(", ") || "—"}]`];
        if (added.length) parts.push(`+[${added.join(", ")}]`);
        if (removed.length) parts.push(`-[${removed.join(", ")}]`);
        this.#logLifecycle("STATE_CHANGED", parts.join("  "), state);
      },
    });

    this.#unsubscribeFn = () => subscription.unsubscribe();
  }

  openDebugDialog(): void {
    if (!this.#registeredKit) return;

    this.dialog.open(AgentDebugDialogComponent, {
      data: { agentKit: this.#registeredKit, agentId: this.#registeredAgentId },
      panelClass: "agent-debug-dialog",
      hasBackdrop: false,
      disableClose: true,
      autoFocus: false,
      restoreFocus: false,
      width: "900px",
      maxWidth: "none",
      height: "80vh",
      maxHeight: "none",
      position: { top: "60px", right: "20px" },
    });
  }

  /** Opens the exclusive "Report chat trace" dialog for the registered agent (no-op before the first container registration). */
  openReportDialog(): void {
    if (!this.#registeredAgentId) return;

    this.dialog.open(ChatReportDialogComponent, {
      width: "560px",
      maxWidth: "90vw",
      autoFocus: false,
      restoreFocus: false,
    });
  }

  /** Records a RUN_STARTED as a fresh "running" entry (duplicates ignored — the agent may re-emit). */
  #traceRunStart(event: unknown): void {
    const e = event as { runId?: string; threadId?: string } | null;
    if (!e?.runId || this.runTrace().some((r) => r.run_id === e.runId)) return;
    this.runTrace.update((runs) => [
      { run_id: e.runId!, thread_id: e.threadId ?? "", status: "running", started_at: new Date().toISOString() },
      ...runs,
    ]);
  }

  /** Marks the matching run's terminal status; a finish without a captured start still lands in the trace. */
  #traceRunEnd(event: unknown, status: "success"): void {
    const e = event as { runId?: string } | null;
    if (!e?.runId) return;
    const runs = this.runTrace();
    const idx = runs.findIndex((r) => r.run_id === e.runId);
    if (idx === -1) {
      this.runTrace.set([{ run_id: e.runId, thread_id: "", status, started_at: new Date().toISOString() }, ...runs]);
      return;
    }
    const next = [...runs];
    next[idx] = { ...next[idx], status };
    this.runTrace.set(next);
  }

  /** RUN_ERROR carries no runId (AG-UI schema) — fails the latest still-running entry. */
  #traceRunError(): void {
    const runs = this.runTrace();
    const idx = runs.findIndex((r) => r.status === "running");
    if (idx === -1) return;
    const next = [...runs];
    next[idx] = { ...next[idx], status: "error" };
    this.runTrace.set(next);
  }

  /** Clears both the Streaming/Lifecycle table and the Background Jobs table (auto-clear on new run / conversation switch clears everything). */
  clear(): void {
    this.events.set([]);
    this.pipelineEvents.set([]);
  }

  clearPipeline(): void {
    this.pipelineEvents.set([]);
  }

  /** Logs a background-job / pipeline SSE item (see BackgroundJobTrackerService) into its own tab, tagged by job id. */
  logPipelineEvent(jobId: string, type: string, content: unknown): void {
    this.#pipelineCounter += 1;
    const item: PipelineDebugEvent = {
      id: this.#pipelineCounter,
      timestamp: new Date(),
      jobId,
      type,
      content,
      searchText: buildSearchText(`${jobId} ${type}`, undefined, content),
    };
    this.pipelineEvents.update((prev) => {
      const next = [item, ...prev];
      return next.length > MAX_PIPELINE_EVENTS ? next.slice(0, MAX_PIPELINE_EVENTS) : next;
    });
  }

  #log(type: string, content: unknown, category: DebugEvent["category"] = "stream", summary?: string): void {
    this.#counter += 1;
    const item: DebugEvent = {
      id: this.#counter,
      timestamp: new Date(),
      type,
      content,
      category,
      summary,
      searchText: buildSearchText(type, summary, content),
    };
    this.events.update((prev) => {
      const next = [item, ...prev];
      return next.length > MAX_EVENTS ? next.slice(0, MAX_EVENTS) : next;
    });
  }

  /** Returns unique non-text block types found anywhere in the snapshot messages. */
  #enrichedBlockTypes(snapshot: unknown): string[] {
    const msgs: unknown[] = (snapshot as any)?.messages ?? (snapshot as any)?.values?.messages ?? [];
    if (!Array.isArray(msgs)) return [];
    const types = new Set<string>();
    for (const m of msgs) {
      const content = (m as any)?.content;
      if (!Array.isArray(content)) continue;
      for (const c of content) {
        const t = (c as any)?.type;
        if (t && t !== "text") types.add(t);
      }
    }
    return Array.from(types);
  }

  #logLifecycle(event: string, detail: string, data: unknown): void {
    this.#log(event, data, "lifecycle", detail);
  }
}
